import {
	IconArrowRight,
	IconBook2,
	IconChevronDown,
	IconClock,
	IconSearch,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { allPosts } from "content-collections";
import { useMemo, useState } from "react";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/blog/")({
	component: RouteComponent,

	head: () => {
		const base = seo({
			title: `Blog | ${config.Name}`,
			description:
				"Thoughts, ideas, and insights on technology, development, and life.",
			keywords:
				"blog, technology, development, programming, software engineering, insights, thoughts",
			url: `${config.Url}/blog`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(schema.blog()),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Blog", url: `${config.Url}/blog` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [categoryOpen, setCategoryOpen] = useState(false);
	const postsPerPage = 8;

	const categories = useMemo(() => {
		const cats = allPosts
			.filter((post) => post.published)
			.map((post) => post.category)
			.filter(Boolean);
		return [...new Set(cats)];
	}, []);

	const allPublishedPosts = useMemo(() => {
		return allPosts
			.filter((post) => post.published)
			.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
	}, []);

	const filteredPosts = useMemo(() => {
		return allPublishedPosts.filter((post) => {
			const matchesSearch =
				searchTerm === "" ||
				post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
				post.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				post.mdx?.toLowerCase().includes(searchTerm.toLowerCase());

			const matchesCategory =
				selectedCategory === "" || post.category === selectedCategory;

			return matchesSearch && matchesCategory;
		});
	}, [allPublishedPosts, searchTerm, selectedCategory]);

	const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
	const startIndex = (currentPage - 1) * postsPerPage;
	const currentPosts = filteredPosts.slice(
		startIndex,
		startIndex + postsPerPage,
	);

	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-50"
				aria-hidden
			/>
			<main className="relative pt-12 sm:pt-20">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Blog"
							description="Thoughts, ideas, and insights on technology, development, and life."
						/>
					</div>

					<div className="pb-20">
						<div
							className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between animate-in fade-in slide-in-from-bottom-8 duration-800 delay-200 fill-mode-both"
							style={{ animationDelay: "0.2s" }}
						>
							<div className="flex items-center gap-3">
								<div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
									<IconBook2 className="size-5" />
								</div>
								<div>
									<h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
										Articles
									</h2>
									<p className="text-sm text-muted-foreground">
										<span className="tabular-nums">{filteredPosts.length}</span>{" "}
										{filteredPosts.length === 1 ? "post" : "posts"}
									</p>
								</div>
							</div>

							<div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
								<div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
									<IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										id="search"
										type="search"
										placeholder="Search posts…"
										value={searchTerm}
										onChange={(e) => {
											setSearchTerm(e.target.value);
											setCurrentPage(1);
										}}
										className="h-10 rounded-lg border-border/80 bg-muted/15 pl-9 dark:bg-muted/10"
									/>
								</div>

								<Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
									<PopoverTrigger asChild>
										<Button
											id="category-filter"
											variant="outline"
											aria-expanded={categoryOpen}
											className="h-10 w-full justify-between rounded-lg border-border/80 bg-background/50 sm:w-[11rem]"
										>
											<span className="truncate">
												{selectedCategory || "All categories"}
											</span>
											<IconChevronDown className="size-4 shrink-0 opacity-60" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="w-[var(--radix-popover-trigger-width)] p-1 sm:w-56"
										align="end"
									>
										<div className="flex flex-col gap-0.5">
											<Button
												variant="ghost"
												onClick={() => {
													setSelectedCategory("");
													setCategoryOpen(false);
													setCurrentPage(1);
												}}
												className="h-9 w-full justify-start font-normal"
											>
												All categories
											</Button>
											{categories.map((category) => (
												<Button
													key={category}
													variant={
														category === selectedCategory
															? "secondary"
															: "ghost"
													}
													onClick={() => {
														setSelectedCategory(category);
														setCategoryOpen(false);
														setCurrentPage(1);
													}}
													className="h-9 w-full justify-start font-normal"
												>
													{category}
												</Button>
											))}
										</div>
									</PopoverContent>
								</Popover>
							</div>
						</div>

						{currentPosts.length > 0 ? (
							<div className="mb-10 grid grid-cols-1 gap-5 md:grid-cols-2">
								{currentPosts.map((post, index) => (
									<div
										key={post._meta.path}
										className="animate-in fade-in slide-in-from-bottom-8 duration-800 delay-400 fill-mode-both min-h-0 min-w-0"
										style={{
											animationDelay: `${0.4 + index * 0.1}s`,
										}}
									>
										<Link
											to="/blog/$path"
											params={{ path: post._meta.path }}
											className="group block min-h-0 min-w-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
										>
											<Card className="relative flex h-full flex-col overflow-hidden border-border/80 bg-card shadow-md transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:shadow-none dark:hover:shadow-none">
												<div
													className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/[0.04] to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100"
													aria-hidden
												/>
												<CardHeader className="relative gap-0 space-y-0 pb-2">
													<div className="mb-3 flex flex-wrap items-center gap-2">
														{post.category ? (
															<Badge
																variant="secondary"
																className="font-normal"
															>
																{post.category}
															</Badge>
														) : null}
														<span className="text-xs text-muted-foreground tabular-nums">
															{post.readingTime}
														</span>
													</div>
													<CardTitle className="line-clamp-2 text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary sm:text-xl">
														{post.title}
													</CardTitle>
													{post.summary ? (
														<CardDescription className="mt-2 line-clamp-3 text-pretty text-base text-muted-foreground sm:text-sm">
															{post.summary}
														</CardDescription>
													) : null}
												</CardHeader>
												<CardContent className="relative mt-auto flex items-center justify-between pt-0">
													{post.date ? (
														<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
															<IconClock className="size-4 shrink-0 text-primary/70" />
															{new Date(post.date).toLocaleDateString("en-US", {
																year: "numeric",
																month: "short",
																day: "numeric",
															})}
														</div>
													) : (
														<span />
													)}
													<span className="flex items-center gap-1 text-sm font-medium text-primary opacity-90 transition-opacity group-hover:opacity-100">
														Read
														<IconArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
													</span>
												</CardContent>
											</Card>
										</Link>
									</div>
								))}
							</div>
						) : (
							<div
								className="rounded-xl border border-dashed border-border/80 bg-muted/10 py-16 text-center animate-in fade-in slide-in-from-bottom-8 duration-800 delay-400 fill-mode-both"
								style={{ animationDelay: "0.4s" }}
							>
								<IconBook2 className="mx-auto mb-4 size-12 text-muted-foreground/50" />
								{allPublishedPosts.length === 0 ? (
									<>
										<h3 className="text-lg font-semibold text-foreground">
											No posts yet
										</h3>
										<p className="mt-2 text-muted-foreground">
											Check back soon for new content.
										</p>
									</>
								) : (
									<>
										<h3 className="text-lg font-semibold text-foreground">
											No results
										</h3>
										<p className="mt-2 text-muted-foreground">
											Try a different search or category.
										</p>
									</>
								)}
							</div>
						)}

						{totalPages > 1 ? (
							<div
								className="flex flex-wrap items-center justify-center gap-2 animate-in fade-in slide-in-from-bottom-8 duration-800 fill-mode-both"
								style={{
									animationDelay: `${0.45 + currentPosts.length * 0.1}s`,
								}}
							>
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										setCurrentPage((prev) => Math.max(prev - 1, 1))
									}
									disabled={currentPage === 1}
									className="rounded-full border-border/80"
								>
									Previous
								</Button>
								<div className="flex flex-wrap justify-center gap-1">
									{Array.from({ length: totalPages }, (_, i) => i + 1).map(
										(page) => (
											<Button
												key={page}
												variant={currentPage === page ? "default" : "outline"}
												size="sm"
												onClick={() => setCurrentPage(page)}
												className={cn(
													"size-9 rounded-full p-0",
													currentPage !== page && "border-border/80",
												)}
											>
												{page}
											</Button>
										),
									)}
								</div>
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										setCurrentPage((prev) => Math.min(prev + 1, totalPages))
									}
									disabled={currentPage === totalPages}
									className="rounded-full border-border/80"
								>
									Next
								</Button>
							</div>
						) : null}
					</div>
				</div>
			</main>
		</div>
	);
}
